import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DOMPurify from "dompurify";
import { 
  Sparkles, Brain, Heart, Award, BookOpen, Users, 
  Play, Calendar, TrendingUp, ArrowRight, Smile, Gift, Megaphone, Check, Flame, Loader2,
  CalendarDays, Video, MessageCircle, Music, Headphones, Crown, Lock, Meh, Frown, ChevronRight, Star, Trophy, Globe
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import AttachmentList from "@/components/AttachmentList";
import { hasFullAccess, PremiumGate } from "@/components/PremiumGate";

// Chart configuration extracted to prevent re-renders
const CHART_GRADIENT = (
  <defs>
    <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor="#C5705D" stopOpacity={0.3}/>
      <stop offset="95%" stopColor="#C5705D" stopOpacity={0}/>
    </linearGradient>
  </defs>
);

const AXIS_TICK_STYLE = { fill: '#5F6D5B', fontSize: 12 };

const TOOLTIP_STYLE = { 
  backgroundColor: '#fff', 
  border: '1px solid #E5E4E0',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(42, 53, 40, 0.1)'
};

const QUICK_ACTIONS = [
  { icon: Brain, label: "AI Coach", path: "/ai-coach", color: "bg-spa-sage/20" },
  { icon: Heart, label: "Mood Check", path: "/mood", color: "bg-spa-terracotta/10" },
  { icon: Sparkles, label: "Mind-SPA", path: "/mind-spa", color: "bg-spa-sky/20" },
  { icon: Users, label: "Community", path: "/community", color: "bg-spa-clay/30" },
];

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dailyRewards, setDailyRewards] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [sessionLinks, setSessionLinks] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [checkinMood, setCheckinMood] = useState(null);
  const [checkinReflection, setCheckinReflection] = useState("");
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const [checkinSuccess, setCheckinSuccess] = useState(null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const [statsRes, annRes, rewardsRes] = await Promise.all([
        apiClient.get('/dashboard/stats'),
        apiClient.get('/announcements?limit=3').catch(() => ({ data: [] })),
        apiClient.get('/coins/daily-status').catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setAnnouncements(annRes.data || []);
      setDailyRewards(rewardsRes.data);

      // Fetch calendar events + session links for new widgets
      apiClient.get('/calendar/events').then(res => {
        const upcoming = (res.data || [])
          .filter(e => e.date && !isNaN(new Date(e.date).getTime()) && new Date(e.date) >= new Date(new Date().toDateString()))
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setCalendarEvents(upcoming);
      }).catch(() => {});
      apiClient.get('/session-links/active').then(res => setSessionLinks((res.data.links || []).slice(0, 3))).catch(() => {});
      apiClient.get('/wellness/streak').then(res => setStreakData(res.data)).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
    
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      fetchDashboardStats();
    }
  }, [location.search, fetchDashboardStats]);

  const claimDailyLogin = async () => {
    setClaiming(true);
    try {
      const res = await apiClient.post('/coins/claim-daily');
      updateUser({ smahoo_coins: res.data.new_balance });
      fetchDashboardStats();
    } catch {
      // already claimed
    } finally {
      setClaiming(false);
    }
  };

  const handleWellnessCheckin = async (withMood = false) => {
    setCheckinLoading(true);
    try {
      const body = {};
      if (withMood && checkinMood) {
        body.mood = checkinMood;
        if (checkinReflection.trim()) body.reflection = checkinReflection.trim();
      }
      const res = await apiClient.post('/wellness/checkin', body);
      updateUser({ smahoo_coins: res.data.new_balance });
      setCheckinSuccess(res.data);
      setShowMoodPicker(false);
      setCheckinMood(null);
      setCheckinReflection("");
      // Refresh streak data
      apiClient.get('/wellness/streak').then(r => setStreakData(r.data)).catch(() => {});
    } catch (err) {
      if (err.response?.status === 400) {
        setCheckinSuccess({ already: true });
      }
    } finally {
      setCheckinLoading(false);
    }
  };

  // Memoize chart data to prevent re-computation
  const moodChartData = useMemo(() => {
    if (stats?.recent_moods?.length) {
      return stats.recent_moods.map((mood, index) => ({
        day: `Day ${7 - index}`,
        mood: mood.mood_score
      })).reverse();
    }
    return [
      { day: "Mon", mood: 7 },
      { day: "Tue", mood: 6 },
      { day: "Wed", mood: 8 },
      { day: "Thu", mood: 7 },
      { day: "Fri", mood: 9 },
      { day: "Sat", mood: 8 },
      { day: "Sun", mood: 8 },
    ];
  }, [stats?.recent_moods]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-spa-terracotta border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isFull = hasFullAccess(user);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="relative overflow-hidden spa-card bg-gradient-to-br from-white to-spa-sage/10 col-span-full" data-testid="welcome-card">
          <div className="relative z-10">
            <p className="overline-text mb-2">Welcome back</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
              {user?.name || "Friend"}
            </h1>
            <p className="text-spa-text-secondary max-w-xl">
              Continue your journey to mental and emotional wellness. 
              Your daily self-care makes a difference.
            </p>
          </div>
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-spa-sage/10 rounded-full blur-2xl"></div>
          <div className="absolute -right-5 -bottom-10 w-32 h-32 bg-spa-terracotta/10 rounded-full blur-xl"></div>
        </div>

        {/* SMAHOO ChatBOTs - Featured AI Agents */}
        <div className="spa-card" data-testid="featured-agents-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-spa-terracotta rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-spa-moss">SMAHOO ChatBOTs</h3>
                <p className="text-xs text-spa-text-secondary">{isFull ? "8 Core RAPsoul.R Signature Ai.2 Agents" : "4 of 8 Agents — Upgrade for all"}</p>
              </div>
            </div>
            <Link to="/ai-agents" className="text-spa-terracotta hover:text-spa-terracotta-dark text-sm flex items-center gap-1" data-testid="view-all-agents-link">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: "RElapse.REset", tag: "Recovery", color: "bg-rose-500", url: "https://chatgpt.com/g/g-686ca8034560819183011bbf96314ed9-relapse-reset-mind-spa" },
              { name: "Daily ME.WELL Mind-SPA", tag: "Life Skills", color: "bg-amber-500", url: "https://chatgpt.com/g/g-68376fd678248191a8a4f82f9b41edf4-smaho-arts-smahoody-live-ai-2" },
              { name: "RAPsoul.R LIVE", tag: "Premium", color: "bg-violet-500", url: "https://chatgpt.com/g/g-6836170ff96c8191b6a40b017745fadb-mr-rapsoul-r-ai-2-live" },
              { name: "Global VILLAGE", tag: "Community", color: "bg-emerald-500", url: "https://chatgpt.com/g/g-6835b29e1f188191be496b26c5b06f9d-smahoo-global-social-village-forum" },
            ].map((a) => (
              <a
                key={a.name}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl p-4 bg-gradient-to-br from-white to-spa-bone border border-spa-border/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-center"
                data-testid={`featured-agent-${a.name.toLowerCase().replace(/[.\s]/g, '-')}`}
              >
                <div className={`w-10 h-10 ${a.color} rounded-xl flex items-center justify-center mx-auto mb-2.5 group-hover:scale-110 transition-transform`}>
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <p className="text-sm font-medium text-spa-moss leading-tight">{a.name}</p>
                <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-spa-sage/10 text-spa-sage">
                  {a.tag}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Rice And Peas For The Soul Online TV */}
        <div className="spa-card" data-testid="rap-tv-widget">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-sm">
              <Play className="w-5 h-5 text-white fill-white" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-spa-moss">Rice And Peas For The Soul Online TV</h3>
              <p className="text-xs text-spa-text-secondary">Wellness streaming from Mr. RAPsoul.R</p>
            </div>
          </div>
          <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: "56.25%" }}>
            <iframe
              className="absolute inset-0 w-full h-full rounded-2xl"
              src="https://www.youtube.com/embed/GUc1YQzvCVo?si=ZXlsav5mWJzT17NC"
              title="Rice And Peas For The Soul Online TV"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <div className="mt-3 text-center">
            <a
              href="https://www.youtube.com/@rapSOULrTV"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-spa-terracotta hover:underline font-medium"
              data-testid="rap-tv-channel-link"
            >
              <Play className="w-3.5 h-3.5" /> Visit RAPsoul.R Online TV YouTube Channel
            </a>
          </div>
        </div>

        {/* Free user: Upgrade teaser */}
        {!isFull && (
          <div className="space-y-6" data-testid="premium-teaser-section">
            <div className="relative overflow-hidden rounded-3xl border border-amber-200/50 bg-gradient-to-r from-amber-50 via-white to-amber-50/50 p-8" data-testid="upgrade-banner">
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-amber-100/50 rounded-full blur-3xl" />
              <div className="relative flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Crown className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="font-serif text-2xl text-spa-moss mb-2">Unlock Your Full Wellness Journey</h3>
                  <p className="text-spa-text-secondary text-sm max-w-lg">
                    Get access to Streaming, all 8 ChatBOTs, OnCALL+ Live ICU, Life Skills, Gift Basket Store, Retreat programs, and more.
                  </p>
                </div>
                <Link to="/pricing" className="spa-button-primary px-6 py-3 gap-2 flex-shrink-0" data-testid="upgrade-banner-btn">
                  <Sparkles className="w-4 h-4" /> View Plans
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PremiumGate variant="card" feature="Streaming" description="YouTube videos & Spotify playlists" />
              <PremiumGate variant="card" feature="OnCALL+ Live ICU" description="24/7 crisis support & live sessions" />
              <PremiumGate variant="card" feature="Life Skills" description="Personal development modules" />
              <PremiumGate variant="card" feature="Gift Basket Store" description="Curated wellness gift baskets" />
              <PremiumGate variant="card" feature="Mind-SPA Retreat" description="Seasonal retreat programs" />
              <PremiumGate variant="card" feature="All 8 ChatBOTs" description="Full AI agent access" />
            </div>
          </div>
        )}

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="space-y-3" data-testid="announcements-section">
            {announcements.map(a => (
              <div key={a.announcement_id} className="spa-card border-l-4 border-l-spa-terracotta bg-gradient-to-r from-spa-terracotta/5 to-transparent" data-testid={`dash-ann-${a.announcement_id}`}>
                <div className="flex items-start gap-3">
                  <Megaphone className="w-5 h-5 text-spa-terracotta mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-spa-moss text-sm">{a.title}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-spa-sage/10 text-spa-sage">{a.category?.replace("_", " ")}</span>
                    </div>
                    <div className="text-spa-text-secondary text-sm prose prose-stone prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.content) }} />
                    <AttachmentList attachments={a.attachments} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Daily Rewards */}
        {dailyRewards && (
          <div className="spa-card border border-amber-200/30 bg-gradient-to-r from-amber-50/50 to-white" data-testid="daily-rewards-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-spa-moss">Daily Rewards</h3>
                  <p className="text-xs text-spa-text-secondary">Earn up to 22 coins today</p>
                </div>
              </div>
              {dailyRewards.streak > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 rounded-full" data-testid="streak-badge">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-orange-600 font-medium text-sm">{dailyRewards.streak} day streak</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dailyRewards.rewards.map((r) => {
                const icons = { daily_login: Award, mood_checkin: Heart, community_post: Users, ai_coaching: Brain };
                const Icon = icons[r.type] || Award;
                return (
                  <div key={r.type} className={`rounded-xl p-3 text-center border transition-all ${r.claimed ? "bg-spa-sage/10 border-spa-sage/30" : "bg-white border-spa-border/50"}`} data-testid={`reward-${r.type}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${r.claimed ? "bg-spa-sage/20" : "bg-amber-50"}`}>
                      {r.claimed ? <Check className="w-4 h-4 text-spa-sage" /> : <Icon className="w-4 h-4 text-amber-500" />}
                    </div>
                    <p className="text-xs text-spa-moss font-medium">{r.label}</p>
                    <p className={`text-xs mt-0.5 ${r.claimed ? "text-spa-sage" : "text-amber-600"}`}>
                      {r.claimed ? "Claimed" : `+${r.coins} coins`}
                    </p>
                    {r.type === "daily_login" && !r.claimed && (
                      <button onClick={claimDailyLogin} disabled={claiming} className="mt-2 px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50" data-testid="claim-daily-btn">
                        {claiming ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Claim"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Wellness Streak Tracker Widget */}
        <div className="spa-card border border-orange-200/30 bg-gradient-to-br from-orange-50/40 via-white to-amber-50/30 overflow-hidden relative" data-testid="wellness-streak-widget">
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-orange-100/40 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-sm">
                  <Flame className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-spa-moss">Wellness Streak</h3>
                  <p className="text-xs text-spa-text-secondary">Check in daily to build your streak</p>
                </div>
              </div>
              <Link to="/wellness-journey" className="text-spa-terracotta hover:text-spa-terracotta-dark text-sm flex items-center gap-1" data-testid="view-journey-link">
                My Journey <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="flex flex-col md:flex-row items-stretch gap-5">
              {/* Streak Display */}
              <div className="flex items-center gap-5 flex-1 min-w-0">
                <div className="text-center">
                  <div className="relative">
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${(streakData?.streak || 0) > 0 ? "bg-gradient-to-br from-orange-400 to-amber-500 shadow-lg shadow-orange-200/50" : "bg-spa-muted"}`}>
                      <span className={`font-serif text-3xl ${(streakData?.streak || 0) > 0 ? "text-white" : "text-spa-text-secondary"}`}>
                        {streakData?.streak || 0}
                      </span>
                    </div>
                    {(streakData?.streak || 0) >= 7 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                        <Star className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-spa-text-secondary mt-2 font-medium">day streak</p>
                </div>

                {/* Mini heatmap (last 7 days) */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-spa-text-secondary mb-2">Last 7 days</p>
                  <div className="flex gap-1.5">
                    {(streakData?.heatmap || []).slice(-7).map((day) => {
                      const dayLabel = new Date(day.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "narrow" });
                      const isToday = day.date === new Date().toISOString().split("T")[0];
                      return (
                        <div key={day.date} className="flex-1 text-center">
                          <div
                            className={`aspect-square rounded-lg flex items-center justify-center mb-1 transition-all
                              ${day.checked_in ? "bg-spa-sage text-white" : "bg-spa-muted text-spa-text-secondary/40"}
                              ${isToday ? "ring-2 ring-spa-terracotta ring-offset-1" : ""}
                            `}
                          >
                            {day.checked_in ? <Check className="w-3 h-3" /> : <span className="text-[10px]">{new Date(day.date + "T00:00:00").getDate()}</span>}
                          </div>
                          <span className="text-[10px] text-spa-text-secondary">{dayLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                  {streakData?.next_milestone && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[11px] text-spa-text-secondary mb-1">
                        <span>Next: {streakData.next_milestone.label}</span>
                        <span className="text-amber-500 font-medium">+{streakData.next_milestone.bonus} coins</span>
                      </div>
                      <div className="h-1.5 bg-orange-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-orange-400 to-amber-400 rounded-full transition-all duration-700" style={{ width: `${Math.min(100, ((streakData?.streak || 0) / streakData.next_milestone.days) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Check-in Action */}
              <div className="flex-shrink-0 md:w-56 md:border-l md:border-orange-200/40 md:pl-5">
                {streakData?.checked_in_today || checkinSuccess ? (
                  <div className="text-center py-3">
                    <div className="w-12 h-12 mx-auto mb-2 bg-spa-sage/20 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-spa-sage" />
                    </div>
                    <p className="text-sm font-medium text-spa-moss">Checked in today!</p>
                    {checkinSuccess && !checkinSuccess.already && (
                      <p className="text-xs text-amber-500 mt-1">+{checkinSuccess.coins_earned} coins earned</p>
                    )}
                    {checkinSuccess?.milestone && (
                      <div className="mt-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200/50">
                        <p className="text-xs font-medium text-amber-600">
                          <Trophy className="w-3 h-3 inline mr-1" />{checkinSuccess.milestone.label}! +{checkinSuccess.milestone_bonus} bonus
                        </p>
                      </div>
                    )}
                  </div>
                ) : showMoodPicker ? (
                  <div className="space-y-3">
                    <p className="text-xs text-spa-text-secondary font-medium">How are you feeling?</p>
                    <div className="grid grid-cols-5 gap-1.5">
                      {[
                        { key: "great", icon: Sparkles, color: "text-emerald-500", bg: "bg-emerald-50", activeBg: "bg-emerald-100 ring-2 ring-emerald-300" },
                        { key: "good", icon: Smile, color: "text-green-500", bg: "bg-green-50", activeBg: "bg-green-100 ring-2 ring-green-300" },
                        { key: "okay", icon: Meh, color: "text-amber-500", bg: "bg-amber-50", activeBg: "bg-amber-100 ring-2 ring-amber-300" },
                        { key: "low", icon: Frown, color: "text-orange-500", bg: "bg-orange-50", activeBg: "bg-orange-100 ring-2 ring-orange-300" },
                        { key: "struggling", icon: Heart, color: "text-rose-500", bg: "bg-rose-50", activeBg: "bg-rose-100 ring-2 ring-rose-300" },
                      ].map(({ key, icon: Icon, color, bg, activeBg }) => (
                        <button
                          key={key}
                          onClick={() => setCheckinMood(key)}
                          className={`p-2 rounded-lg transition-all ${checkinMood === key ? activeBg : `${bg} hover:scale-105`}`}
                          title={key}
                          data-testid={`mood-${key}`}
                        >
                          <Icon className={`w-4 h-4 mx-auto ${color}`} />
                        </button>
                      ))}
                    </div>
                    <input
                      value={checkinReflection}
                      onChange={(e) => setCheckinReflection(e.target.value)}
                      placeholder="Brief reflection (optional)"
                      className="w-full px-3 py-2 text-xs rounded-lg border border-spa-border/50 bg-white focus:outline-none focus:ring-1 focus:ring-spa-terracotta/30"
                      data-testid="checkin-reflection-input"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleWellnessCheckin(true)}
                        disabled={!checkinMood || checkinLoading}
                        className="flex-1 px-3 py-2 bg-gradient-to-r from-orange-400 to-amber-500 text-white rounded-lg text-xs font-medium hover:from-orange-500 hover:to-amber-600 transition-all disabled:opacity-50"
                        data-testid="checkin-with-mood-btn"
                      >
                        {checkinLoading ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : "+8 coins"}
                      </button>
                      <button
                        onClick={() => { setShowMoodPicker(false); setCheckinMood(null); setCheckinReflection(""); }}
                        className="px-3 py-2 border border-spa-border/50 rounded-lg text-xs text-spa-text-secondary hover:bg-spa-muted transition-colors"
                        data-testid="checkin-cancel-btn"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <button
                      onClick={() => handleWellnessCheckin(false)}
                      disabled={checkinLoading}
                      className="w-full px-4 py-3 bg-gradient-to-r from-orange-400 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-orange-500 hover:to-amber-600 transition-all shadow-sm shadow-orange-200/50 disabled:opacity-50"
                      data-testid="quick-checkin-btn"
                    >
                      {checkinLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
                        <span className="flex items-center justify-center gap-2">
                          <Flame className="w-4 h-4" /> Check In <span className="opacity-70">+5</span>
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setShowMoodPicker(true)}
                      className="w-full px-4 py-2.5 border border-orange-200/60 text-orange-600 rounded-xl text-xs font-medium hover:bg-orange-50/50 transition-colors"
                      data-testid="mood-checkin-btn"
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        <Heart className="w-3.5 h-3.5" /> Add Mood +8 coins
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="spa-card" data-testid="coins-balance-card">
            <div className="flex items-center justify-between mb-3">
              <Award className="w-6 h-6 text-spa-terracotta" strokeWidth={1.5} />
              <span className="text-xs text-spa-text-secondary uppercase tracking-wider">Coins</span>
            </div>
            <p className="font-serif text-3xl text-spa-moss coins-glow">
              {stats?.smahoo_coins?.toFixed(0) || 0}
            </p>
            <p className="text-sm text-spa-text-secondary mt-1">SMAHOO Coins</p>
          </div>

          <div className="spa-card" data-testid="mood-avg-card">
            <div className="flex items-center justify-between mb-3">
              <Smile className="w-6 h-6 text-spa-sage" strokeWidth={1.5} />
              <span className="text-xs text-spa-text-secondary uppercase tracking-wider">Mood</span>
            </div>
            <p className="font-serif text-3xl text-spa-moss">
              {stats?.average_mood?.toFixed(1) || "—"}/10
            </p>
            <p className="text-sm text-spa-text-secondary mt-1">Weekly Average</p>
          </div>

          <div className="spa-card" data-testid="chat-sessions-card">
            <div className="flex items-center justify-between mb-3">
              <Brain className="w-6 h-6 text-spa-sky" strokeWidth={1.5} />
              <span className="text-xs text-spa-text-secondary uppercase tracking-wider">Coach</span>
            </div>
            <p className="font-serif text-3xl text-spa-moss">
              {stats?.chat_sessions || 0}
            </p>
            <p className="text-sm text-spa-text-secondary mt-1">AI Sessions</p>
          </div>

          <div className="spa-card" data-testid="community-posts-card">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-6 h-6 text-spa-clay" strokeWidth={1.5} />
              <span className="text-xs text-spa-text-secondary uppercase tracking-wider">Community</span>
            </div>
            <p className="font-serif text-3xl text-spa-moss">
              {stats?.community_posts || 0}
            </p>
            <p className="text-sm text-spa-text-secondary mt-1">Posts Created</p>
          </div>
        </div>

        {/* New Feature Widgets — Calendar, Streaming, Connect */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="feature-widgets">
          {/* Calendar Widget — Free */}
          <Link to="/calendar" className="group relative overflow-hidden rounded-3xl border border-spa-border bg-white p-6 shadow-sm hover:-translate-y-1 transition-all duration-300" data-testid="dash-calendar-widget">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-spa-sage/10 rounded-full blur-xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-spa-sage/20 rounded-xl flex items-center justify-center group-hover:bg-spa-sage/30 transition-colors">
                  <CalendarDays className="w-5 h-5 text-spa-sage" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-lg text-spa-moss">Calendar</h3>
                  <p className="text-[11px] text-spa-text-secondary">Your upcoming schedule</p>
                </div>
                <ArrowRight className="w-4 h-4 text-spa-text-secondary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </div>
              {calendarEvents.length > 0 ? (
                <div className="space-y-2.5">
                  {calendarEvents.map((evt, i) => (
                    <div key={evt.event_id || `ce-${i}`} className="flex items-center gap-3">
                      <div className="text-center min-w-[36px]">
                        <p className="text-[10px] text-spa-terracotta font-medium uppercase leading-tight">
                          {evt.date ? new Date(evt.date + "T00:00:00").toLocaleDateString("en-US", { month: "short" }) : ""}
                        </p>
                        <p className="text-lg font-serif text-spa-moss leading-tight">
                          {evt.date ? new Date(evt.date + "T00:00:00").getDate() : ""}
                        </p>
                      </div>
                      <div className="w-0.5 h-8 bg-spa-sage/40 rounded-full" />
                      <div className="min-w-0">
                        <p className="text-sm text-spa-moss font-medium truncate">{evt.title}</p>
                        <p className="text-[11px] text-spa-text-secondary">{evt.time || ""}{evt.location ? ` · ${evt.location}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-spa-text-secondary">No upcoming events</p>
              )}
            </div>
          </Link>

          {/* Streaming Widget — Premium only or teaser */}
          {isFull ? (
            <Link to="/streaming" className="group relative overflow-hidden rounded-3xl border border-spa-border bg-white p-6 shadow-sm hover:-translate-y-1 transition-all duration-300" data-testid="dash-streaming-widget">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-red-100/50 rounded-full blur-xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-red-50 rounded-xl flex items-center justify-center group-hover:bg-red-100 transition-colors">
                    <Play className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-spa-moss">Streaming</h3>
                    <p className="text-[11px] text-spa-text-secondary">YouTube & Spotify</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-spa-text-secondary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-red-50/50">
                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                      <Video className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-sm text-spa-moss font-medium">YouTube Videos</p>
                      <p className="text-[11px] text-spa-text-secondary">Guided therapy & workshops</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-green-50/50">
                    <div className="w-8 h-8 bg-[#1DB954] rounded-lg flex items-center justify-center">
                      <Headphones className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-sm text-spa-moss font-medium">Spotify Playlists</p>
                      <p className="text-[11px] text-spa-text-secondary">Meditation & healing audio</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ) : (
            <PremiumGate variant="card" feature="Streaming" description="YouTube videos & Spotify playlists" />
          )}

          {/* Connect Widget — Free */}
          <div className="relative overflow-hidden rounded-3xl border border-spa-border bg-white p-6 shadow-sm" data-testid="dash-connect-widget">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-blue-100/50 rounded-full blur-xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Video className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-lg text-spa-moss">Connect</h3>
                  <p className="text-[11px] text-spa-text-secondary">Zoom & WhatsApp</p>
                </div>
              </div>
              {sessionLinks.length > 0 ? (
                <div className="space-y-2.5">
                  {sessionLinks.map(link => (
                    <a
                      key={link.link_id}
                      href={link.link_type === "zoom" ? link.url : `https://wa.me/${link.whatsapp_number}${link.whatsapp_message ? `?text=${encodeURIComponent(link.whatsapp_message)}` : ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${link.link_type === "zoom" ? "bg-blue-50/50 hover:bg-blue-50" : "bg-green-50/50 hover:bg-green-50"}`}
                      data-testid={`dash-link-${link.link_id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${link.link_type === "zoom" ? "bg-blue-500" : "bg-[#25D366]"}`}>
                        {link.link_type === "zoom" ? <Video className="w-4 h-4 text-white" strokeWidth={2} /> : <MessageCircle className="w-4 h-4 text-white" strokeWidth={2} />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-spa-moss font-medium truncate">{link.title}</p>
                        <p className="text-[11px] text-spa-text-secondary truncate">{link.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <Link to="/sessions" className="text-sm text-spa-terracotta flex items-center gap-1 hover:underline">
                  View Sessions <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Mood Chart */}
          <div className="lg:col-span-2 spa-card" data-testid="mood-chart-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-serif text-xl text-spa-moss">Mood Journey</h3>
                <p className="text-sm text-spa-text-secondary">Your emotional wellness this week</p>
              </div>
              <Link 
                to="/mood" 
                className="text-spa-terracotta hover:text-spa-terracotta-dark text-sm flex items-center gap-1"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={moodChartData}>
                  {CHART_GRADIENT}
                  <XAxis 
                    dataKey="day" 
                    axisLine={false} 
                    tickLine={false}
                    tick={AXIS_TICK_STYLE}
                  />
                  <YAxis 
                    domain={[0, 10]} 
                    axisLine={false} 
                    tickLine={false}
                    tick={AXIS_TICK_STYLE}
                  />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area 
                    type="monotone" 
                    dataKey="mood" 
                    stroke="#C5705D" 
                    strokeWidth={2}
                    fill="url(#moodGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="spa-card" data-testid="quick-actions-card">
            <h3 className="font-serif text-xl text-spa-moss mb-6">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              {QUICK_ACTIONS.map((action) => (
                <Link
                  key={action.path}
                  to={action.path}
                  className={`${action.color} rounded-2xl p-4 hover:scale-105 transition-transform duration-300 flex flex-col items-center gap-2`}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <action.icon className="w-6 h-6 text-spa-moss" strokeWidth={1.5} />
                  <span className="text-sm text-spa-moss font-medium">{action.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        {isFull && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Gift Basket Store Featured */}
          <Link to="/gift-store" className="spa-card group bg-gradient-to-br from-spa-terracotta/10 to-spa-clay/20 border-spa-terracotta/20" data-testid="gift-store-card">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-spa-terracotta rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Gift className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-lg text-spa-moss mb-1">Gift Basket Store</h3>
              <p className="text-xs text-spa-text-secondary mb-3">mySMAHOO.Online Self-CARE</p>
              <span className="text-spa-terracotta text-sm flex items-center gap-1">
                Shop Now <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </Link>

          <Link to="/life-skills" className="spa-card group" data-testid="life-skills-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-spa-sage/20 rounded-xl flex items-center justify-center group-hover:bg-spa-terracotta/10 transition-colors">
                <BookOpen className="w-6 h-6 text-spa-moss" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-lg text-spa-moss mb-1">Life Skills Academy</h3>
                <p className="text-sm text-spa-text-secondary">Courses on stress management, resilience, and behavioral change</p>
              </div>
            </div>
          </Link>

          <Link to="/streaming" className="spa-card group" data-testid="streaming-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-spa-sky/20 rounded-xl flex items-center justify-center group-hover:bg-spa-terracotta/10 transition-colors">
                <Play className="w-6 h-6 text-spa-moss" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-lg text-spa-moss mb-1">Streaming Services</h3>
                <p className="text-sm text-spa-text-secondary">Rice And Peas For The Soul TV and wellness content</p>
              </div>
            </div>
          </Link>

          <Link to="/sessions" className="spa-card group" data-testid="sessions-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-spa-clay/30 rounded-xl flex items-center justify-center group-hover:bg-spa-terracotta/10 transition-colors">
                <Calendar className="w-6 h-6 text-spa-moss" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-lg text-spa-moss mb-1">RAPsoul.R OnCALL+</h3>
                <p className="text-sm text-spa-text-secondary">Book live sessions with Mr. RAPsoul.R</p>
              </div>
            </div>
          </Link>
        </div>
        )}

        {/* WHO Mental Health Action Plan Widget */}
        <div className="spa-card border border-spa-moss/10 bg-gradient-to-r from-spa-moss/5 via-white to-spa-sage/5 overflow-hidden" data-testid="who-widget">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-14 h-14 bg-spa-moss rounded-2xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-serif text-lg text-spa-moss">WHO Mental Health Action Plan 2013–2030</h3>
                <span className="px-2 py-0.5 bg-spa-terracotta/10 text-spa-terracotta rounded-full text-[10px] font-medium">
                  {(() => {
                    const days = Math.max(0, Math.floor((new Date('2030-12-31') - new Date()) / 86400000));
                    return `${Math.floor(days / 365)}y ${days % 365}d remaining`;
                  })()}
                </span>
              </div>
              <p className="text-xs text-spa-text-secondary leading-relaxed">
                This app answers WHO Objective 1 — connecting global mental health policy with everyday community action. You are part of this movement.
              </p>
            </div>
            <a
              href="https://www.who.int/initiatives/mental-health-action-plan-2013-2030"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 px-4 py-2 border border-spa-moss/20 rounded-xl text-xs font-medium text-spa-moss hover:bg-spa-moss/5 transition-colors"
              data-testid="who-learn-more"
            >
              Learn More
            </a>
          </div>
        </div>

        {/* Subscription Banner — shown only for free users who haven't already seen the teaser */}
        {!isFull && (
          <div className="spa-card bg-gradient-to-r from-spa-terracotta to-spa-terracotta-dark text-white" data-testid="bottom-upgrade-banner">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl mb-1">Ready to Transform Your Wellness?</h3>
                <p className="text-white/80 text-sm">Start from just CA$9.99/month — AI coaching, streaming, community, and more</p>
              </div>
              <Link 
                to="/pricing"
                className="bg-white text-spa-terracotta px-6 py-2 rounded-full font-medium hover:bg-white/90 transition-colors whitespace-nowrap"
                data-testid="bottom-upgrade-btn"
              >
                View Plans
              </Link>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
